import { useEffect, useRef } from "react";
import styled from "styled-components";
import { TextField } from "@mui/material";
import { Text } from "../Text";

const InputContainer = styled.div`
  width: ${(p) => (p.width ? p.width : "100%")};
  flex: ${(p) => (p.flex ? p.flex : "unset")};
  position: relative;
  font-family: NotoSansHebrew;
`;

const StyleStartImg = styled.img(() => ({
  width: 20,
  height: 20,
  marginInlineEnd: "1em",
  alignSelf: "center",
  position: "absolute",
  right: 10,
  top: 10,
}));

const StyleEndImg = styled.img(({ isRtl, error, hasValue }) => ({
  marginInlineStart: "auto",
  width: 20,
  height: 20,
  alignSelf: "center",
  position: "absolute",
  left: hasValue ? 40 : 20,
  right: !isRtl && 40,
  top: error ? 10 : 10,
}));

const StyleResetImg = styled.img(({ isRtl, error }) => ({
  marginInlineStart: "auto",
  width: 20,
  height: 20,
  alignSelf: "center",
  position: "absolute",
  left: isRtl && 10,
  right: !isRtl && 10,
  top: error ? 10 : 10,
}));

const StyledInput = styled(TextField)`
  && {
    .MuiOutlinedInput-root {
      border-radius: 4px;
      font-family: NotoSansHebrew;
      background: white !important;
      background-color: white !important;
      text-align: ${(p) => (p.multiline ? "start" : "center")};
      align-items: ${(p) => (p.multiline ? "flex-start" : "center")};
      border: 0;
      color: rgba(0, 0, 0, 0.47);
      margin-top: ${(p) => (p.style?.border ? "0" : "inhrite")};
    }

    .MuiOutlinedInput-notchedOutline {
      border: ${(p) => (p.style?.border ? 0 : "inhrite")};
    }
    .MuiFormHelperText-root.Mui-error {
      text-align: ${(p) => (p.$isRtl ? "right" : "left")};
      margin-top: ${(p) => (p.style?.border ? "5px" : "inhrite")};
    }

    .MuiInputBase-input {
      text-align: start;
      font-size: ${(p) => (p.fontSize ? p.fontSize : "inherit")};
      text-indent: ${(p) => (p.$withStartImg ? "20px" : "10px")};
      padding: ${(p) => (p.style?.border ? 0 : "inhrite")};
      padding-inline-start: ${(p) => (p.style?.border ? "15px" : "inhrite")};
    }

    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
      color: #8c89ac !important;
    }

    input[type="password"] {
      letter-spacing: 9px;
      width: 70%;
    }
  }
`;

const Label = styled(Text)`
  font-weight: ${(p) => (p.shrink ? "400" : "unset")};
  font-size: ${(p) => (p.shrink ? "13px !important" : "unset")};
  padding: ${(p) => (p.shrink ? "6px" : "unset")};
  display: ${(p) => (p.shrink ? "block" : "none")};
  transform-origin: ${(p) => (p.shrink ? "top left" : "unset")};
  white-space: ${(p) => (p.shrink ? "nowrap" : "unset")};
  overflow: ${(p) => (p.shrink ? "hidden" : "unset")};
  text-overflow: ${(p) => (p.shrink ? "ellipsis" : "unset")};
  position: ${(p) => (p.shrink ? "absolute" : "unset")};
  right: ${(p) => (p.shrink && p.isRtl ? "12px" : "unset")};
  left: ${(p) => (p.shrink && !p.isRtl ? "12px" : "unset")};
  top: ${(p) => (p.shrink ? "-14px" : "unset")};
  background-color: ${(p) => (p.shrink ? "#ffffff" : "unset")};
  z-index: ${(p) => (p.shrink ? "999" : "unset")};
  color: ${(p) => (p.shrink ? "#8C89AC" : "unset")};
`;

const inputLabelProps = {
  shrink: true,
  margin: "2rem",
};

const Input = ({
  label,
  placeholder,
  width,
  isRtl,
  fontSize,
  validation,
  errorText,
  value,
  shrink,
  onReset,
  endImg,
  startImg,
  resetImg,
  onEndImgClick,
  ...props
}) => {
  useEffect(() => {}, [value]);
  const inputRef = useRef(null);

  const handleFocus = (e) => {
    if (props.onFocus) props.onFocus(e);

    // Ensure focus and then select — especially for mobile browsers
    requestAnimationFrame(() => {
      const el = inputRef.current?.querySelector("input, textarea");
      if (el && typeof el.select === "function") {
        el.focus();
        el.setSelectionRange(0, el.value.length); // safer than select() on mobile
      }
    });
  };

  return (
    <InputContainer width={width} flex={props.style.flex}>
      {label && (
        <Label isRtl={isRtl} shrink={shrink.toString()}>
          {label}
        </Label>
      )}
      <StyledInput
        inputRef={inputRef}
        onFocus={handleFocus}
        variant="outlined"
        InputLabelProps={inputLabelProps}
        placeholder={placeholder || label}
        fullWidth={width === "full"}
        size="small"
        fontSize={props.style.fontSize}
        error={!!errorText || props.error}
        helperText={props.helperText}
        $isRtl={true}
        rows={4}
        $withStartImg={!!startImg}
        onChange={props.onChange}
        onKeyUp={props.onKeyPress}
        options={props.options}
        style={props.style}
        type={props.type}
        value={value}
        inputProps={{ maxLength: props.maxLength }}
      />
      {!!startImg && <StyleStartImg src={startImg} alt="input-start-img" />}
      {!!endImg && (
        <StyleEndImg
          error={!!errorText || props.error}
          src={endImg}
          isRtl={isRtl}
          hasValue={!!value}
          onClick={onEndImgClick}
          alt="input-end-img"
        />
      )}
      {!!resetImg && !!value && (
        <StyleResetImg
          error={!!errorText || props.error}
          src={resetImg}
          isRtl={isRtl}
          alt="clear-input"
          onClick={() => onReset()}
        />
      )}
    </InputContainer>
  );
};

export default Input;
